import React, { useState } from 'react';
import { ChangelogTimeline } from './ChangelogTimeline';
import { SessionTracker } from './SessionTracker';
import { BookOpen, Clock, FileText, Download, Eye } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

type TabType = 'CHANGELOG' | 'SESSIONS' | 'CONTEXT';

export const VersionManager: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('CHANGELOG');

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="border-b pb-4" style={{ borderColor: 'var(--border-color)' }}>
                <h1 className="text-3xl font-serif font-bold mb-2" style={{ color: 'var(--accent-glow)' }}>
                    Versioning & Sessions
                </h1>
                <p style={{ color: 'var(--text-secondary)' }}>
                    Suivez l'évolution du projet et gérez vos sessions de travail
                </p>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 p-1 rounded-xl border inline-flex" style={{
                backgroundColor: 'var(--glass-bg)',
                borderColor: 'var(--glass-border)'
            }}>
                <button
                    onClick={() => setActiveTab('CHANGELOG')}
                    className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium flex items-center gap-2`}
                    style={activeTab === 'CHANGELOG' ? {
                        backgroundColor: 'var(--accent-color)',
                        color: 'var(--bg-primary)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    } : {
                        color: 'var(--text-secondary)'
                    }}
                >
                    <BookOpen className="w-4 h-4" />
                    Changelog
                </button>
                <button
                    onClick={() => setActiveTab('SESSIONS')}
                    className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium flex items-center gap-2`}
                    style={activeTab === 'SESSIONS' ? {
                        backgroundColor: 'var(--accent-color)',
                        color: 'var(--bg-primary)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    } : {
                        color: 'var(--text-secondary)'
                    }}
                >
                    <Clock className="w-4 h-4" />
                    Sessions
                </button>
                <button
                    onClick={() => setActiveTab('CONTEXT')}
                    className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium flex items-center gap-2`}
                    style={activeTab === 'CONTEXT' ? {
                        backgroundColor: 'var(--accent-color)',
                        color: 'var(--bg-primary)',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    } : {
                        color: 'var(--text-secondary)'
                    }}
                >
                    <FileText className="w-4 h-4" />
                    Contexte Projet
                </button>
            </div>

            {/* Content */}
            <div className="animate-fade-in-up">
                {activeTab === 'CHANGELOG' && <ChangelogTimeline />}
                {activeTab === 'SESSIONS' && <SessionTracker />}
                {activeTab === 'CONTEXT' && <ContextFiles />}
            </div>
        </div>
    );
};

// Context Files Component
const ContextFiles: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [fileContent, setFileContent] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const contextFiles = [
        {
            name: 'PROJECT_STATUS.md',
            description: 'État actuel du projet, fonctionnalités, bugs connus, dernières modifications',
            icon: '📊',
        },
        {
            name: 'ARCHITECTURE.md',
            description: 'Structure technique, stack technologique, flux de données, composants clés',
            icon: '🏗️',
        },
        {
            name: 'ROADMAP.md',
            description: 'Feuille de route, priorités court/moyen/long terme, workflows Antigravity',
            icon: '🗺️',
        },
        {
            name: 'CHANGELOG.md',
            description: 'Historique complet des versions avec toutes les modifications',
            icon: '📜',
        },
        {
            name: 'README.md',
            description: 'Guide principal d\'installation, utilisation et documentation',
            icon: '📖',
        },
    ];

    const handleViewFile = async (filename: string) => {
        setLoading(true);
        setSelectedFile(filename);
        try {
            const response = await api.get(`/admin/context/${filename}`);
            setFileContent(response.data.content);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erreur lors du chargement du fichier');
            setSelectedFile(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadFile = async (filename: string) => {
        try {
            const response = await api.get(`/admin/context/${filename}`);
            const blob = new Blob([response.data.content], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            toast.success(`${filename} téléchargé !`);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erreur lors du téléchargement');
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-serif font-bold mb-2" style={{ color: 'var(--accent-glow)' }}>
                    Fichiers de Contexte pour Antigravity
                </h2>
                <p style={{ color: 'var(--text-secondary)' }}>
                    Ces fichiers sont <span className="font-bold" style={{ color: 'var(--accent-color)' }}>protégés</span> et accessibles uniquement aux administrateurs.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {contextFiles.map((file) => (
                    <div
                        key={file.name}
                        className={`card-navy border-l-4 transition-all ${selectedFile === file.name ? 'ring-2' : ''
                            }`}
                        style={{
                            borderLeftColor: selectedFile === file.name ? 'var(--accent-color)' : 'var(--accent-glow)',
                            ...(selectedFile === file.name && {
                                boxShadow: `0 0 0 2px var(--accent-color)`
                            })
                        }}
                    >
                        <div className="flex items-start gap-4">
                            <div className="text-4xl">{file.icon}</div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                                    {file.name}
                                </h3>
                                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{file.description}</p>
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() => handleViewFile(file.name)}
                                        disabled={loading && selectedFile === file.name}
                                        className="px-3 py-1 text-white text-xs font-medium rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                                        style={{ backgroundColor: '#3b82f6' }}
                                    >
                                        <Eye className="w-3 h-3" />
                                        Voir
                                    </button>
                                    <button
                                        onClick={() => handleDownloadFile(file.name)}
                                        className="px-3 py-1 text-white text-xs font-medium rounded transition-colors flex items-center gap-1"
                                        style={{ backgroundColor: '#10b981' }}
                                    >
                                        <Download className="w-3 h-3" />
                                        Télécharger
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* File Viewer */}
            {selectedFile && fileContent && (
                <div className="card-navy animate-fade-in-up">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold" style={{ color: 'var(--accent-glow)' }}>{selectedFile}</h3>
                        <button
                            onClick={() => {
                                setSelectedFile(null);
                                setFileContent('');
                            }}
                            className="transition-colors"
                            style={{ color: 'var(--text-secondary)' }}
                        >
                            ✕ Fermer
                        </button>
                    </div>
                    <div className="rounded-lg p-4 max-h-96 overflow-y-auto border" style={{
                        backgroundColor: 'var(--bg-primary)',
                        borderColor: 'var(--border-color)'
                    }}>
                        <pre className="text-sm whitespace-pre-wrap font-mono" style={{ color: 'var(--text-secondary)' }}>
                            {fileContent}
                        </pre>
                    </div>
                </div>
            )}

            {/* Security Notice */}
            <div className="card-vintage border-2" style={{ borderColor: 'rgba(16, 185, 129, 0.3)' }}>
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--bg-primary)' }}>
                    🔒 Sécurité Renforcée
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--bg-primary)' }}>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#10b981' }}>✓</span>
                        <span>Accès restreint aux administrateurs uniquement</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#10b981' }}>✓</span>
                        <span>Protection contre les attaques de traversée de chemin</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#10b981' }}>✓</span>
                        <span>Whitelist de fichiers autorisés</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#10b981' }}>✓</span>
                        <span>Logs d'audit pour chaque accès</span>
                    </li>
                </ul>
            </div>

            {/* Usage Guide */}
            <div className="card-vintage border-2" style={{ borderColor: 'rgba(59, 130, 246, 0.3)' }}>
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--bg-primary)' }}>
                    💡 Comment utiliser ces fichiers ?
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--bg-primary)' }}>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#3b82f6' }}>1.</span>
                        <span>Les agents Antigravity lisent automatiquement ces fichiers au début de chaque session</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#3b82f6' }}>2.</span>
                        <span>Mettez à jour <code className="px-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>PROJECT_STATUS.md</code> après chaque modification importante</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#3b82f6' }}>3.</span>
                        <span><code className="px-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>ARCHITECTURE.md</code> et <code className="px-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>ROADMAP.md</code> sont plus stables</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="font-bold" style={{ color: '#3b82f6' }}>4.</span>
                        <span>Le <code className="px-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>CHANGELOG.md</code> est mis à jour à chaque release</span>
                    </li>
                </ul>
            </div>
        </div>
    );
};
